import React from "react";
import { type EditorProps } from "../../utility";
import { useArsListing } from "./ArsListingContextProvider";
import { ARSL_CELL_UPDATE, HEADER_ROW_ID, type ArsTextAlignment } from "./interface";

export interface ArsListingCellProps<T = string> extends EditorProps<T> {
  rowId?: string;
  colId?: string;
}

/**
 * Lightweight read-only cell renderer used by scaffold definitions.
 *
 * @param props - Scaffold editor props
 * @param props.id - Unique scaffold-generated cell identifier
 * @param props.colId - Column identifier passed by ScaffoldCell
 * @param props.rowId - Row identifier passed by ScaffoldCell
 * @param props.setValue - Scaffold-provided value setter (not used in read-only cell)
 * @param props.value - Cell value from scaffold cell data
 * @returns A span showing the cell value with text alignment applied
 */
export const ArsListingCell = <T,>(props: ArsListingCellProps<T>): React.ReactElement => {
  const { state, dispatch } = useArsListing();
  const { id, colId, rowId, value } = props;
  const isHeaderRow = rowId === HEADER_ROW_ID;
  const column = state?.bodyData?.arsListingColumns?.find((col) => col.id === colId);

  const textAlign: ArsTextAlignment = isHeaderRow
    ? (column?.headerTextAlignment ?? "left")
    : (column?.bodyTextAlignment ?? "left");

  const Editor = state?.settings.editorProps.Editor;

  return !Editor || !rowId || !colId ? (
    <></>
  ) : state.settings.disabled ||
    (state.settings.tlfViewMode.value === "ANNOTATIONS" && isHeaderRow) ||
    state.bodyData.arsBodyRows.find((row) => row.id === rowId)?.ellipsis ? (
    <span
      id={id}
      style={{
        display: "block",
        width: "100%",
        textAlign: textAlign,
      }}
    >
      {state?.settings.editorProps.getTextFromT(value).join("\n")}
    </span>
  ) : (
    <Editor
      id={id}
      value={value}
      setValue={(newValue) => {
        dispatch({
          operation: ARSL_CELL_UPDATE,
          rowId,
          colId,
          value: newValue,
        });
      }}
      editable={true}
      textAlignment={textAlign}
    />
  );
};

ArsListingCell.displayName = "ArsListingCell";
